/**
 * 3D 보기용 모형 — 2D 도면·재단표와 같은 계산 결과(패널 중심선·천장 배치·문 개구부)를 상자 목록으로 바꿈
 * 좌표: 도면 mm 그대로 (x = 가로, z = 도면 y(아래쪽 +), y = 높이). 그리는 쪽(Plan3D)에서 m로 줄임
 */
import { PolyPanel, Pt, Opening } from '../engine/polyEngine';
import { CeilingPanel, Door, Drain } from '../types';
import { numberPanels, ceilingNo, floorNo } from './panelNumbers';
import { jointLabel } from './productionSheet';

export type BoxKind = 'wall' | 'part' | 'ceiling' | 'floor' | 'door' | 'frame' | 'concrete';

export interface Box3 {
  kind: BoxKind;
  /** 가운데 (mm) */
  cx: number; cy: number; cz: number;
  /** 길이(진행 방향) · 높이 · 두께 (mm) */
  len: number; h: number; t: number;
  /** y축 회전 (rad) — 길이 방향이 x축에서 돈 각도 */
  rot: number;
  /** 패널 번호·설명 (누르면 보임) — 콘크리트 등은 없음 */
  no?: string;
  info?: string;
  /** 재단 패널 (표준 900·반절 아님) */
  cut?: boolean;
}

export interface Plan3DInput {
  outline: Pt[];                 // 패널 바깥면 다각형 (시계방향)
  panels: PolyPanel[];           // 벽·하리 안쪽·칸막이 (칸막이 pieceKey 'part:')
  PT: number;
  wallHeight: number;
  std: number; half: number;
  ceiling: CeilingPanel[];
  ceilingDir: 'H' | 'V';         // H = 패널 길이가 가로
  floor: boolean;
  doors: Door[];
  doorOpenings: Opening[];       // doorsToOpenings 결과 (id = 문 id)
  existing: Opening[];           // 기존 벽 (edge 키 + 구간)
  drains: Drain[];
  H: number; V: number;
}

export interface Plan3DModel { boxes: Box3[]; drains: { x: number; z: number; r: number }[]; H: number; V: number; top: number }

const SEAM = 3; // 패널 사이 이음선이 보이게 길이에서 뺌 (mm)

/** 점이 다각형 안인가 (짝홀 규칙) */
export function inside(p: Pt, poly: Pt[]): boolean {
  let c = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i], b = poly[j];
    if ((a.y > p.y) !== (b.y > p.y) && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) c = !c;
  }
  return c;
}

/** 축 정렬 사각형 ∩ 직각 다각형 → 사각형들 (다각형 꼭짓점 좌표로 칸을 나눠 안쪽 칸만, 가로로 이어 붙임) */
export function clipRect(r: { x0: number; x1: number; y0: number; y1: number }, poly: Pt[]) {
  const cut = (vals: number[], lo: number, hi: number) => Array.from(new Set([lo, hi, ...vals.filter(v => v > lo && v < hi)])).sort((a, b) => a - b);
  const xs = cut(poly.map(p => p.x), r.x0, r.x1), ys = cut(poly.map(p => p.y), r.y0, r.y1);
  const out: { x0: number; x1: number; y0: number; y1: number }[] = [];
  for (let j = 0; j + 1 < ys.length; j++) {
    let start: number | null = null;
    for (let i = 0; i + 1 < xs.length; i++) {
      const isIn = inside({ x: (xs[i] + xs[i + 1]) / 2, y: (ys[j] + ys[j + 1]) / 2 }, poly);
      if (isIn && start === null) start = xs[i];
      if ((!isIn || i + 2 === xs.length) && start !== null) {
        out.push({ x0: start, x1: isIn ? xs[i + 1] : xs[i], y0: ys[j], y1: ys[j + 1] });
        start = null;
      }
    }
  }
  return out;
}

const flat = (x0: number, x1: number, z0: number, z1: number, y0: number, y1: number): Pick<Box3, 'cx' | 'cy' | 'cz' | 'len' | 'h' | 't' | 'rot'> =>
  ({ cx: (x0 + x1) / 2, cy: (y0 + y1) / 2, cz: (z0 + z1) / 2, len: x1 - x0, h: y1 - y0, t: z1 - z0, rot: 0 });

export function buildPlan3D(inp: Plan3DInput): Plan3DModel {
  const { PT, wallHeight: WH } = inp;
  const boxes: Box3[] = [];
  const nos = numberPanels(inp.panels);

  // 벽·칸막이: 중심선 a→b, 두께 = 패널 두께
  inp.panels.forEach(p => {
    const dx = p.b.x - p.a.x, dy = p.b.y - p.a.y, L = Math.hypot(dx, dy);
    const part = p.pieceKey.startsWith('part:');
    const cut = !(p.size === inp.std || p.size === inp.half);
    boxes.push({
      kind: part ? 'part' : 'wall', cx: (p.a.x + p.b.x) / 2, cy: WH / 2, cz: (p.a.y + p.b.y) / 2,
      len: Math.max(1, L - SEAM), h: WH, t: p.thick ?? PT, rot: -Math.atan2(dy, dx),
      no: nos[p.id], cut, info: `${part ? '칸막이' : '벽'} · ${p.size} × ${WH} · ${jointLabel(p.startType, p.endType)}${cut ? ' · 재단' : ''}`,
    });
  });

  // 천장(벽 위) · 바닥(벽 아래): 패널 줄을 외곽 다각형으로 잘라 (ㄱ자·하리 홈 빈 곳은 빼고)
  const strips = (kind: 'ceiling' | 'floor', y0: number, y1: number) => inp.ceiling.forEach((c, i) => {
    const r = inp.ceilingDir === 'H'
      ? { x0: 0, x1: c.length, y0: c.pos, y1: c.pos + c.size }
      : { x0: c.pos, x1: c.pos + c.size, y0: 0, y1: c.length };
    const no = kind === 'ceiling' ? ceilingNo(i) : floorNo(i);
    const edge = i === 0 && c.size !== inp.std && !c.isCustom;
    const cut = !(c.size === inp.std || edge);
    // 이음선: 나누는 방향으로만 조금 줄임
    const s = SEAM / 2;
    clipRect(r, inp.outline).forEach(q => boxes.push({
      kind, ...flat(q.x0 + (inp.ceilingDir === 'V' ? s : 0), q.x1 - (inp.ceilingDir === 'V' ? s : 0),
        q.y0 + (inp.ceilingDir === 'H' ? s : 0), q.y1 - (inp.ceilingDir === 'H' ? s : 0), y0, y1),
      no, cut, info: `${kind === 'ceiling' ? '천장' : '바닥'} · ${c.size} × ${c.length} · ${jointLabel(c.leftType, c.rightType)}${cut ? ' · 재단' : ''}`,
    }));
  });
  if (inp.ceiling.length) strips('ceiling', WH, WH + PT);
  if (inp.floor && inp.ceiling.length) strips('floor', -PT, 0);

  // 문: 개구부 [from, to] = 왼쪽 문틀 패널 + 문짝 + 오른쪽 문틀 패널. 벽 중심선은 이웃 패널에서 찾음 (홈 안쪽 변이어도 맞게)
  inp.doorOpenings.forEach(o => {
    const d = inp.doors.find(x => x.id === o.id);
    if (!d || !o.side) return;
    const horiz = o.side === 'top' || o.side === 'bottom';
    const near = inp.panels.filter(p => !p.pieceKey.startsWith('part:') && (horiz ? Math.abs(p.a.y - p.b.y) < 1 : Math.abs(p.a.x - p.b.x) < 1))
      .filter(p => [p.a, p.b].some(q => Math.min(Math.abs((horiz ? q.x : q.y) - o.from), Math.abs((horiz ? q.x : q.y) - o.to)) < 40));
    const fallback = { top: PT / 2, bottom: inp.V - PT / 2, left: PT / 2, right: inp.H - PT / 2 }[o.side];
    // 같은 x(y)에 끝이 닿는 패널은 맞은편 벽에도 있음 → 그 면(위·아래·왼·오른) 쪽에 가장 가까운 줄
    const lines = near.map(p => (horiz ? p.a.y : p.a.x)).sort((p, q) => Math.abs(p - fallback) - Math.abs(q - fallback));
    const line = lines.length ? lines[0] : fallback;
    const seg = (a: number, b: number, y0: number, y1: number, t: number, kind: BoxKind, extra: Partial<Box3> = {}) => {
      const m = (a + b) / 2;
      boxes.push({ kind, cx: horiz ? m : line, cz: horiz ? line : m, cy: (y0 + y1) / 2, len: Math.max(1, b - a - SEAM), h: y1 - y0, t, rot: horiz ? 0 : Math.PI / 2, ...extra });
    };
    const info = `문 · ${d.width} × ${d.height}${d.doorType === 'double' ? ' · 양개' : ''}`;
    const l0 = o.from, l1 = l0 + d.leftPanel, r0 = l1 + d.width, r1 = o.to;
    seg(l0, l1, 0, WH, PT, 'frame', { info: `문틀 · ${d.leftPanel}` });
    seg(r0, r1, 0, WH, PT, 'frame', { info: `문틀 · ${d.rightPanel}` });
    if (d.height < WH) seg(l1, r0, d.height, WH, PT, 'frame', { info: `문 위 · ${d.width} × ${WH - d.height}` });
    if (d.doorType === 'double') {
      seg(l1, l1 + d.width / 2, 0, d.height, PT * 0.7, 'door', { info });
      seg(l1 + d.width / 2, r0, 0, d.height, PT * 0.7, 'door', { info });
    } else seg(l1, r0, 0, d.height, PT * 0.7, 'door', { info });
  });

  // 기존 벽: 패널 없는 구간 — 바깥쪽에 콘크리트 벽 (2D와 같이 두께 1.5PT)
  const keyOf = (a: Pt, b: Pt) => `${Math.min(a.x, b.x)},${Math.min(a.y, b.y)}-${Math.max(a.x, b.x)},${Math.max(a.y, b.y)}`;
  const pts = inp.outline, n = pts.length;
  inp.existing.filter(o => o.kind === 'existing' && o.edge).forEach(o => {
    for (let i = 0; i < n; i++) {
      const a = pts[i], b = pts[(i + 1) % n];
      if (keyOf(a, b) !== o.edge) continue;
      const horiz = a.y === b.y, L = Math.hypot(b.x - a.x, b.y - a.y);
      const out = { x: (b.y - a.y) / L, y: -(b.x - a.x) / L }; // 시계방향: 바깥 = 진행 왼쪽
      const t0 = Math.min(o.from, o.to), t1 = Math.max(o.from, o.to), w = PT * 1.5, m = (t0 + t1) / 2;
      boxes.push({ kind: 'concrete', cx: horiz ? m : a.x + out.x * w / 2, cz: horiz ? a.y + out.y * w / 2 : m, cy: (WH + PT) / 2,
        len: t1 - t0, h: WH + PT, t: w, rot: horiz ? 0 : Math.PI / 2, info: '기존 벽 (패널 없음)' });
    }
  });

  // 배수구 (바닥 위 원)
  const drains = inp.drains.map(d => ({ x: d.x, z: d.y, r: Math.max(40, d.size / 2) }));
  return { boxes, drains, H: inp.H, V: inp.V, top: WH + (inp.ceiling.length ? PT : 0) };
}
